/**
 * VistoeraTASK-00529 - pure helpers for 积分管理 / 使用统计 (no React, unit-tested
 * by scripts/teamCreditsUsage.node-test.ts). Display only: every limit and every
 * number is validated / computed by the backend again.
 */
import type { CategoryBucket, UsageCategory, UsageRange } from './teamTypes'

export const MAX_MEMBER_LIMIT = 99_999_999
export const RANGE_KEYS: readonly UsageRange[] = ['month', '7d', '30d']
export const CATEGORY_KEYS: readonly UsageCategory[] = ['image', 'video', 'text', 'audio', 'other']

export type LimitInputError = 'required' | 'invalid' | 'negative' | 'tooLarge' | 'precision' | 'exceedsTeam'

export type LimitInput = { unlimited: true } | { unlimited: false; value: number | null | undefined }

/** Mirror of the backend validation; returns the request body or the i18n error key. */
export function buildLimitPayload(
  input: LimitInput,
  teamAvailable?: string | number | null,
): { ok: true; payload: { unlimited: true } | { monthly_limit: number } } | { ok: false; error: LimitInputError } {
  if (input.unlimited) return { ok: true, payload: { unlimited: true } }
  const v = input.value
  if (v === null || v === undefined) return { ok: false, error: 'required' }
  if (typeof v !== 'number' || !Number.isFinite(v)) return { ok: false, error: 'invalid' }
  if (v < 0) return { ok: false, error: 'negative' }
  if (v > MAX_MEMBER_LIMIT) return { ok: false, error: 'tooLarge' }
  // the ledger keeps four decimals
  if (Math.abs(Math.round(v * 10_000) / 10_000 - v) > 1e-9) return { ok: false, error: 'precision' }
  // VistoeraTASK-00558: a finite cap may not exceed the team's currently available credits
  // (immediate hint only - the backend re-checks against the live balance).
  if (exceedsTeamAvailable(v, teamAvailable)) return { ok: false, error: 'exceedsTeam' }
  return { ok: true, payload: { monthly_limit: v } }
}

/** `true` only when both numbers are known and the requested finite limit is above the team's available credits. */
export function exceedsTeamAvailable(value: number | null | undefined, teamAvailable: string | number | null | undefined): boolean {
  if (value === null || value === undefined || teamAvailable === null || teamAvailable === undefined) return false
  const avail = Number(teamAvailable)
  if (!Number.isFinite(value) || !Number.isFinite(avail)) return false
  return value > avail
}

/** Share of the limit already used, 0-100; `null` when there is no meaningful ratio (unlimited / zero limit). */
export function usagePercent(used: string | number, limit: string | number | null): number | null {
  if (limit === null) return null
  const u = Number(used)
  const l = Number(limit)
  if (!Number.isFinite(u) || !Number.isFinite(l) || l <= 0) return null
  return Math.max(0, Math.min(100, Math.round((u / l) * 100)))
}

/** Percent of a bucket within the whole (integer, 0 when the whole is empty - never NaN). */
export function sharePercent(part: number, whole: number): number {
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole <= 0) return 0
  return Math.round((part / whole) * 100)
}

export function totalCount(items: CategoryBucket[]): number {
  return items.reduce((n, i) => n + i.count, 0)
}

/** Known product names are i18n keys; anything else falls back to the raw key; `null` = 未知. */
export function providerKeyOf(providerKey: string | null): string {
  return providerKey && providerKey.trim() ? providerKey.trim().toLowerCase() : 'unknown'
}

/** Provider filter value sent to the API (the backend treats "unknown" as the empty provider). */
export function providerFilterValue(providerKey: string | null): string {
  return providerKeyOf(providerKey)
}

export function isUsageRange(v: unknown): v is UsageRange {
  return typeof v === 'string' && (RANGE_KEYS as readonly string[]).includes(v)
}
